import { NextRequest, NextResponse } from 'next/server';
import { db } from '@/lib/db';
import bcrypt from 'bcryptjs';

export async function POST(request: NextRequest) {
  try {
    const body = await request.json();

    // Validate required fields
    const {
      firstName,
      lastName,
      email,
      password,
      phone,
      location,
      dateOfBirth,
      bio,
      profilePhoto,
      certifications,
      yearsOfExperience,
      specializations,
      servicesOffered,
      availability,
      preferredEnvironment,
    } = body;

    // Step 1 validation
    if (!firstName || !lastName || !email || !password || !phone || !location || !dateOfBirth) {
      return NextResponse.json(
        { error: 'Missing required fields: firstName, lastName, email, password, phone, location, dateOfBirth' },
        { status: 400 }
      );
    }

    // Password validation
    if (password.length < 8) {
      return NextResponse.json(
        { error: 'Password must be at least 8 characters' },
        { status: 400 }
      );
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return NextResponse.json(
        { error: 'Invalid email format' },
        { status: 400 }
      );
    }

    // Age validation (must be 18+)
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    if (age < 18) {
      return NextResponse.json(
        { error: 'You must be at least 18 years old to register as a trainer' },
        { status: 400 }
      );
    }

    // Step 2 validation
    if (!bio || bio.trim().length < 100) {
      return NextResponse.json(
        { error: 'Bio must be at least 100 characters' },
        { status: 400 }
      );
    }

    if (!profilePhoto) {
      return NextResponse.json(
        { error: 'Profile photo is required' },
        { status: 400 }
      );
    }

    if (!certifications || certifications.length === 0) {
      return NextResponse.json(
        { error: 'At least one certification is required' },
        { status: 400 }
      );
    }

    if (!yearsOfExperience || yearsOfExperience < 0) {
      return NextResponse.json(
        { error: 'Years of experience is required' },
        { status: 400 }
      );
    }

    // Step 3 validation
    if (!specializations || specializations.length === 0) {
      return NextResponse.json(
        { error: 'At least one specialization must be selected' },
        { status: 400 }
      );
    }

    if (!servicesOffered || servicesOffered.length === 0) {
      return NextResponse.json(
        { error: 'At least one service must be selected' },
        { status: 400 }
      );
    }

    if (!availability) {
      return NextResponse.json(
        { error: 'Availability is required' },
        { status: 400 }
      );
    }

    // Check if user already exists
    const existingUser = await db.users.findByEmail(email);
    if (existingUser) {
      return NextResponse.json(
        { error: 'Email already registered' },
        { status: 400 }
      );
    }

    // Hash password
    const passwordHash = await bcrypt.hash(password, 10);

    // Create user account
    await db.users.create({
      fullName: `${firstName} ${lastName}`,
      email,
      phone,
      passwordHash,
      role: 'trainer',
      status: 'active'
    });

    // Create trainer in database
    const trainer = await db.trainers.create({
      ownerEmail: email,
      firstName,
      lastName,
      phone,
      location,
      dateOfBirth: birthDate,
      bio,
      profilePhoto,
      certifications,
      yearsOfExperience: parseInt(yearsOfExperience),
      specializations,
      servicesOffered,
      sessionPricing: body.sessionPricing || null,
      availability,
      preferredEnvironment: preferredEnvironment || [],
      travelDistance: body.travelDistance || null,
      additionalNotes: body.additionalNotes || null,
      status: 'pending', // Trainers need admin approval
    });

    // TODO: Send confirmation email to trainer
    // TODO: Send notification to admin for approval

    return NextResponse.json({
      success: true,
      message: 'Trainer registered successfully. Awaiting admin approval.',
      trainer: {
        id: trainer.id,
        firstName: trainer.firstName,
        lastName: trainer.lastName,
        status: trainer.status,
        createdAt: trainer.createdAt,
      },
    });
  } catch (error) {
    console.error('Trainer registration error:', error);
    
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('Error details:', errorMessage);
    
    return NextResponse.json(
      { 
        error: 'Failed to register trainer. Please try again.',
        details: process.env.NODE_ENV === 'development' ? errorMessage : undefined
      },
      { status: 500 }
    );
  }
}
